/**
 * UnitsService.ts — OPERATOR UNITS™ (Build 2.00.2)
 *
 * SINGLE RESPONSIBILITY:
 *   The ONE source of truth for all unit conversions and formatted display
 *   strings throughout WorkRoamer. Every module that displays a measurement
 *   MUST use this service — no page or component may perform its own
 *   unit calculations.
 *
 * CANONICAL UNITS (always pass these as input to formatters):
 *   Temperature → Celsius  (°C)
 *   Distance    → Kilometres (km)
 *   Speed       → Kilometres per hour (km/h)
 *
 * ARCHITECTURE RULES:
 *   • Pure TypeScript — no React imports.
 *   • Reads operator preference from loadOperatorConfig() on every call so
 *     changes are always reflected without a restart.
 *   • Subscribes to the existing `nomad:occ-saved` CustomEvent for live
 *     listener notification (same pattern as Home.tsx / legacyBridge.ts).
 *   • Never stores converted values — converts fresh on every call.
 *   • No external APIs, no network calls, no localStorage writes.
 *
 * FUTURE MODULES:
 *   Weather, Wallet, Travel Costs, Maps, Passport, Stay, Visa, Health,
 *   Marketplace, Reports — all import `unitsService` from this file and call
 *   `formatTemperature(celsius)` / `formatDistance(km)` / `formatSpeed(kph)`.
 */

import {
  loadOperatorConfig,
  type TemperatureUnit,
  type DistanceUnit,
  type SpeedUnit,
  type CurrencyDisplayMode,
  type OperatorUnitsConfig,
} from '@/lib/operatorConfig';

// ─── Public types ─────────────────────────────────────────────────────────────

export type { TemperatureUnit, DistanceUnit, SpeedUnit, CurrencyDisplayMode, OperatorUnitsConfig };

export interface UnitsDiagnostics {
  temperature:      TemperatureUnit;
  distance:         DistanceUnit;
  speed:            SpeedUnit;
  currencyDisplay:  CurrencyDisplayMode;
  configSource:     string;
  lastConversion:   string;
}

export type UnitsListener = () => void;

// ─── Conversion constants ─────────────────────────────────────────────────────

const KM_TO_MILES = 0.621371;
const MILES_TO_KM = 1 / KM_TO_MILES;

// ─── Service ──────────────────────────────────────────────────────────────────

class UnitsService {
  private readonly listeners = new Set<UnitsListener>();
  private _lastConversion   = 'none';

  // ── Config ───────────────────────────────────────────────────────────────────

  /** Current operator unit preferences — always fresh from OCC config. */
  getConfig(): OperatorUnitsConfig {
    const cfg = loadOperatorConfig();
    // Deep-merge ensures the field is present even on old stored configs that
    // pre-date this sprint (loadOperatorConfig deep-merges with DEFAULT_OCC_CONFIG).
    return cfg.units;
  }

  // ── Subscriptions ─────────────────────────────────────────────────────────────

  /**
   * Subscribe to unit-preference changes.  Fires whenever the operator saves
   * the OCC config.  Returns an unsubscribe function.
   */
  onChange(fn: UnitsListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Called by the `nomad:occ-saved` window event wired below. */
  notifyListeners(): void {
    this.listeners.forEach(fn => fn());
  }

  // ── Pure conversions ──────────────────────────────────────────────────────────

  /** Celsius → Fahrenheit */
  celsiusToFahrenheit(celsius: number): number {
    return (celsius * 9) / 5 + 32;
  }

  /** Fahrenheit → Celsius */
  fahrenheitToCelsius(fahrenheit: number): number {
    return ((fahrenheit - 32) * 5) / 9;
  }

  /** Kilometres → Miles */
  kmToMiles(km: number): number {
    return km * KM_TO_MILES;
  }

  /** Miles → Kilometres */
  milesToKm(miles: number): number {
    return miles * MILES_TO_KM;
  }

  /** km/h → mph */
  kphToMph(kph: number): number {
    return kph * KM_TO_MILES;
  }

  /** mph → km/h */
  mphToKph(mph: number): number {
    return mph * MILES_TO_KM;
  }

  // ── Formatters ────────────────────────────────────────────────────────────────

  /**
   * Format a temperature value for display.
   * @param celsius  Canonical input in degrees Celsius.
   * @returns        Display string such as "29°C" or "84°F".
   */
  formatTemperature(celsius: number): string {
    this._lastConversion = `temperature(${celsius}°C)`;
    const unit = this.getConfig().temperature;
    if (unit === 'fahrenheit') {
      return `${Math.round(this.celsiusToFahrenheit(celsius))}°F`;
    }
    return `${Math.round(celsius)}°C`;
  }

  /**
   * Format a distance value for display.
   * @param km        Canonical input in kilometres.
   * @param decimals  Decimal places (default 1).
   * @returns         Display string such as "5.0 km" or "3.1 mi".
   */
  formatDistance(km: number, decimals = 1): string {
    this._lastConversion = `distance(${km}km)`;
    const unit = this.getConfig().distance;
    if (unit === 'miles') {
      return `${this.kmToMiles(km).toFixed(decimals)} mi`;
    }
    return `${km.toFixed(decimals)} km`;
  }

  /**
   * Format a speed value for display.
   * @param kph  Canonical input in km/h.
   * @returns    Display string such as "80 km/h" or "50 mph".
   */
  formatSpeed(kph: number): string {
    this._lastConversion = `speed(${kph}kph)`;
    const unit = this.getConfig().speed;
    if (unit === 'mph') {
      return `${Math.round(this.kphToMph(kph))} mph`;
    }
    return `${Math.round(kph)} km/h`;
  }

  /**
   * Short temperature unit symbol ("°C" or "°F") — useful as a standalone label.
   */
  temperatureSymbol(): string {
    return this.getConfig().temperature === 'fahrenheit' ? '°F' : '°C';
  }

  /**
   * Short distance unit label ("mi" or "km").
   */
  distanceLabel(): string {
    return this.getConfig().distance === 'miles' ? 'mi' : 'km';
  }

  /**
   * Short speed unit label ("mph" or "km/h").
   */
  speedLabel(): string {
    return this.getConfig().speed === 'mph' ? 'mph' : 'km/h';
  }

  // ── Diagnostics ───────────────────────────────────────────────────────────────

  getDiagnostics(): UnitsDiagnostics {
    const cfg = this.getConfig();
    return {
      temperature:     cfg.temperature,
      distance:        cfg.distance,
      speed:           cfg.speed,
      currencyDisplay: cfg.currencyDisplay,
      configSource:    'OCC config — nomad:occ:config:v1 (localStorage)',
      lastConversion:  this._lastConversion,
    };
  }
}

// ─── Singleton export ─────────────────────────────────────────────────────────

export const unitsService = new UnitsService();

// Wire to the OCC save event so onChange listeners fire on every preference
// change — matches the pattern used by Home.tsx and legacyBridge.ts.
if (typeof window !== 'undefined') {
  window.addEventListener('nomad:occ-saved', () => unitsService.notifyListeners());
}

// ─── Standalone diagnostics helper ───────────────────────────────────────────

export function getUnitsDiagnostics(): UnitsDiagnostics {
  return unitsService.getDiagnostics();
}
